import { CommonModule, DatePipe } from '@angular/common';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Component, inject, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Appointment, ServiceRequest } from 'fhir/r4';
import { AdmittedPatientsComponent } from '../admitted-patients/admitted-patients.component';
import { backendEndPointToken, terminologyCanonicalUrlBaseToken } from '../app.config';
import { DisableCountParam, DisableSortParam, LoadingUIEnabled, SkipHospitalSegregation } from '../loading.interceptor';
import { ReferenceDisplayDirective } from '../shared/reference-display.directive';
import { EmptyStateComponent } from '../shared/empty-state/empty-state.component';
import { NaPipe } from '../shared/na.pipe';
import { ChipsDirective } from '../chips.directive';
import { TableHeaderComponent } from '../table-header/table-header.component';
import { CodeableConcept2Pipe } from '../shared/codeable-concept2.pipe';
import { PatientNamePipe } from '../shared/pipes/patient-name.pipe';
import { PatientContactsPipe } from '../shared/pipes/patient-contacts.pipe';
import { PatientAgePipe } from '../shared/pipes/patient-age.pipe';
import { PatientAddressPipe } from '../shared/pipes/patient-address.pipe';
import { UtilityService } from '../shared/utility.service';
import { AppointmentService } from '../services/appointment.service';
import { SuccessToastService } from '../shared/success-toast.service';
import { AppointmentDetailsComponent } from '../appointment/appointment-details/appointment-details.component';
import { DetailsBuilderObject } from '../detailz-viewz/details-builder.service';
import { catchError, forkJoin, map, of } from 'rxjs';

type ConsultationRequestRow = ServiceRequest & {
    linkedAppointment?: Appointment;
    practitionerAppointmentStatus?: string;
    patientId?: string | null;
    patientReference?: string | null;
    hasSharedDocument?: boolean;
    referralIdentifier?: string | null;
    localShellPatientId?: string | null;
};

@Component({
    selector: 'app-consultation-requests',
    standalone: true,
    imports: [
        CommonModule,
        MatCardModule,
        MatButtonModule,
        MatIconModule,
        MatDividerModule,
        DatePipe,
        MatTableModule,
        MatTooltipModule,
        ReferenceDisplayDirective,
        EmptyStateComponent,
        NaPipe,
        ChipsDirective,
        TableHeaderComponent,
        CodeableConcept2Pipe
    ],
    providers: [PatientNamePipe, PatientContactsPipe, PatientAgePipe, PatientAddressPipe],
    templateUrl: './consultation-requests.component.html',
    styleUrls: ['../admitted-patients/admitted-patients.component.scss', '../service-requests/service-requests.component.scss']
})
export class ConsultationRequestsComponent extends AdmittedPatientsComponent implements OnInit {
    private utility = inject(UtilityService);
    private appointmentService = inject(AppointmentService);
    private successToast = inject(SuccessToastService);
    private readonly terminologyBaseUrl = inject(terminologyCanonicalUrlBaseToken);
    private readonly shellLookupBackendUrl = inject(backendEndPointToken);
    private readonly referralIdentifierSystem = `${this.terminologyBaseUrl}/NamingSystem/referral-identifier`;

    override displayedColumns = ['authoredOn', 'status', 'category', 'code', 'subject', 'appointment', 'action'];

    private readonly appointmentDetailsBuilder: DetailsBuilderObject = {
        resourceName: 'Appointment',
        resourceIcon: 'calendar_today',
        specialHeader: {
            strongSectionKey: 'description',
            iconSectionKeys: ['status'],
            contentSectionKeys: ['start', 'end']
        },
        groups: [
            { groupName: 'General', groupIcon: 'info', groupKeys: ['status', 'appointmentType', 'reasonCode'] },
            { groupName: 'Schedule', groupIcon: 'schedule', groupKeys: ['start', 'end', 'minutesDuration'] },
            { groupName: 'Participants', groupIcon: 'people', groupKeys: ['participant'] },
            { groupName: 'Notes', groupIcon: 'notes', groupKeys: ['comment', 'patientInstruction'] }
        ]
    };

    override ngOnInit() {
        this.loadConsultationRequests();
    }

    loadConsultationRequests() {
        const user = this.auth.user.getValue();
        const practitionerId = user?.['fhirResourceId'] || user?.['fhirResourceId']
// $#$        // /* alert(`Practitioner ID: ${practitionerId}`); */ // Debugging line to check practitionerId
        if (!practitionerId) return;

        this.utility.threeTierQueryAndSyncToStateOnce$<ServiceRequest>({
            resourceType: 'ServiceRequest',
            scope: 'orgWide',
            querySearchObject: [
                { key: 'performer.reference', value: [`Practitioner/${practitionerId}`, `${this.backendUrl}/Practitioner/${practitionerId}`] },
                { key: 'status', value: ['active', 'on-hold', 'completed'] }
            ],
            urlSearchObject: {
                performer: `Practitioner/${practitionerId}`,

            },
            context: new HttpContext().set(SkipHospitalSegregation, true).set(LoadingUIEnabled, true)
        }).subscribe(requests => {
            const rows: ConsultationRequestRow[] = requests.map(r => {
                const patientReference = this.normalizeReference(r.subject?.reference);
                return {
                    ...r,
                    id: r.id,
                    patientReference,
                    patientId: this.extractPatientId(patientReference),
                    hasSharedDocument: this.hasSharedDocumentReference(r),
                    referralIdentifier: this.extractReferralIdentifier(r)
                };
            });
            this.resolveLocalShellPatientIds(rows).subscribe((resolvedRows) => this.attachLinkedAppointments(resolvedRows));
        });
    }

    private resolveLocalShellPatientIds(rows: ConsultationRequestRow[]) {
        const lookupRows = rows.filter(row => row.hasSharedDocument && !!row.referralIdentifier);
        if (!lookupRows.length) {
            return of(rows);
        }

        const context = new HttpContext()
            .set(LoadingUIEnabled, false)
            .set(DisableSortParam, true)
            .set(DisableCountParam, true);

        const lookups = lookupRows.map((row) => {
            const identifierValue = `${this.referralIdentifierSystem}|${row.referralIdentifier}`;
            const url = `${this.shellLookupBackendUrl}/Patient?identifier=${encodeURIComponent(identifierValue)}&_count=20`;
            return this.http.get<any>(url, { context }).pipe(
                map((bundle) => ({ rowId: row.id || '', patientId: this.extractPatientIdFromBundle(bundle, row.referralIdentifier || '') })),
                catchError(() => of({ rowId: row.id || '', patientId: null }))
            );
        });

        return forkJoin(lookups).pipe(
            map((results) => {
                const localShellByRowId = new Map(results.map((result) => [result.rowId, result.patientId]));
                return rows.map((row) => ({
                    ...row,
                    localShellPatientId: localShellByRowId.get(row.id || '') || null
                }));
            })
        );
    }

    private attachLinkedAppointments(rows: ConsultationRequestRow[]): void {
        const serviceRequestIds = rows.map(row => row.id || '').filter(Boolean);

        if (!serviceRequestIds.length) {
            this.tableDataSource.data = rows;
            return;
        }

        this.appointmentService.getAppointmentsForServiceRequests(serviceRequestIds).subscribe({
            next: (appointments) => {
                const appointmentByServiceRequestRef = new Map<string, Appointment>();

                for (const appointment of appointments) {
                    for (const basedOn of appointment.basedOn ?? []) {
                        const reference = (basedOn.reference ?? '').trim();
                        if (reference.startsWith('ServiceRequest/')) {
                            appointmentByServiceRequestRef.set(reference, appointment);
                        }
                    }
                }

                const practitionerRef = this.currentPractitionerReference();
                this.tableDataSource.data = rows.map(row => {
                    const linkedAppointment = row.id ? appointmentByServiceRequestRef.get(`ServiceRequest/${row.id}`) : undefined;
                    return {
                        ...row,
                        linkedAppointment,
                        practitionerAppointmentStatus: this.extractParticipantStatus(linkedAppointment, practitionerRef)
                    };
                });
            },
            error: () => {
                this.tableDataSource.data = rows;
            }
        });
    }

    appointmentStatusLabel(row: ConsultationRequestRow): string {
        if (!row.linkedAppointment) {
            return 'Awaiting booking';
        }

        return row.practitionerAppointmentStatus || row.linkedAppointment.status || 'unknown';
    }

    canRespondToLinkedAppointment(row: ConsultationRequestRow): boolean {
        return !!row.linkedAppointment?.id && (row.practitionerAppointmentStatus ?? '').toLowerCase() === 'needs-action';
    }

    openLinkedAppointment(row: ConsultationRequestRow, event?: Event): void {
        event?.stopPropagation();
        if (!row.linkedAppointment) {
            return;
        }

        this.dialog.open(AppointmentDetailsComponent, {
            maxHeight: '93vh',
            maxWidth: '90vh',
            data: {
                resourceData: row.linkedAppointment,
                detailsBuilderObject: this.appointmentDetailsBuilder
            }
        });
    }

    acceptLinkedAppointment(row: ConsultationRequestRow, event?: Event): void {
        event?.stopPropagation();
        const appointmentId = row.linkedAppointment?.id;
        if (!appointmentId) {
            return;
        }

        this.appointmentService.respondToAppointment(appointmentId, { status: 'accepted' }).subscribe({
            next: () => {
                this.successToast.success('Appointment accepted successfully.');
                this.loadConsultationRequests();
            },
            error: () => {
                this.errorService.openandCloseError('Failed to accept the linked appointment.');
            }
        });
    }

    declineLinkedAppointment(row: ConsultationRequestRow, event?: Event): void {
        event?.stopPropagation();
        const appointmentId = row.linkedAppointment?.id;
        if (!appointmentId) {
            return;
        }

        this.appointmentService.respondToAppointment(appointmentId, { status: 'declined' }, 'Declined from consultation inbox').subscribe({
            next: () => {
                this.successToast.success('Appointment declined successfully.');
                this.loadConsultationRequests();
            },
            error: () => {
                this.errorService.openandCloseError('Failed to decline the linked appointment.');
            }
        });
    }

    private currentPractitionerReference(): string {
        const practitionerId = this.auth.user.getValue()?.['fhirResourceId'];
        return practitionerId ? `Practitioner/${practitionerId}` : '';
    }

    private extractParticipantStatus(appointment: Appointment | undefined, practitionerReference: string): string {
        if (!appointment || !practitionerReference) {
            return '';
        }

        const participant = (appointment.participant ?? []).find(item => item.actor?.reference === practitionerReference);
        return participant?.status || '';
    }

    openRequestDocument(row: ServiceRequest) {
        if (!row.id) {
            return;
        }

        this.router.navigate(['/app', 'request-documents', row.id]);
    }

    openRow(row: ConsultationRequestRow): void {
        if (row.hasSharedDocument) {
            this.openRequestDocument(row);
        }
    }

    canOpenPatientSummary(row: ConsultationRequestRow): boolean {
        return !!(row.localShellPatientId || (!row.hasSharedDocument ? row.patientId : null));
    }

    openPatientSummary(row: ConsultationRequestRow, event?: Event): void {
        event?.stopPropagation();
        const patientId = row.localShellPatientId || row.patientId;
        if (!patientId) {
            return;
        }
        this.router.navigate(['/app', 'patients', patientId, 'summary']);
    }

    private extractReferralIdentifier(request: ServiceRequest): string | null {
        const identifier = (request.identifier ?? []).find(item => `${item.system ?? ''}`.includes('referral-identifier'))?.value;
        return identifier || request.requisition?.value || null;
    }

    private extractPatientIdFromBundle(bundle: any, referralIdentifier: string): string | null {
        const entries = Array.isArray(bundle?.entry) ? bundle.entry : [];
        const patient = entries
            .map((entry: any) => entry.resource)
            .find((resource: any) => resource?.resourceType === 'Patient' && this.patientHasReferralIdentifier(resource, referralIdentifier));
        return patient?.id || null;
    }

    private patientHasReferralIdentifier(patient: any, referralIdentifier: string): boolean {
        return Array.isArray(patient?.identifier)
            && patient.identifier.some((identifier: any) => `${identifier?.system ?? ''}`.includes('referral-identifier') && `${identifier?.value ?? ''}`.trim() === referralIdentifier);
    }

    private hasSharedDocumentReference(request: ServiceRequest): boolean {
        return (request.supportingInfo ?? []).some(ref => {
            const normalized = this.normalizeReference(ref.reference);
            return normalized?.startsWith('DocumentReference/');
        });
    }

    private extractPatientId(reference?: string | null): string | null {
        if (!reference) {
            return null;
        }
        const parts = reference.split('/');
        if (parts.length === 2 && parts[0] === 'Patient' && parts[1]) {
            return parts[1];
        }
        return null;
    }

    private normalizeReference(reference?: string | null): string | null {
        if (!reference) {
            return null;
        }
        const clean = reference.split('?')[0]?.trim() ?? '';
        if (!clean) {
            return null;
        }
        if (!clean.includes('/')) {
            return clean;
        }
        const parts = clean.split('/').filter(Boolean);
        if (parts.length >= 2) {
            const type = parts[parts.length - 2];
            const id = parts[parts.length - 1];
            return `${type}/${id}`;
        }
        return clean;
    }
}
